import * as THREE from 'three';
import { Ray } from './Ray.js';
import { Hit } from './Hit.js';
import { SplatRenderMode } from '../SplatRenderMode.js';

const PICK_OPACITY_THRESHOLD = 0.05;
const MAX_OPACITY_RADIUS_MULTIPLIER = 10.0;

export class Raycaster {

    static MEASUREMENT_PICK_OPTIONS = {
        useExpandedPickRadius: false,
        allowClosestApproach: false,
        pickRadiusMultiplier: 1.0,
        minimumPickAlphaFloor: 20
    };

    constructor(origin, direction, raycastAgainstTrueSplatEllipsoid = false, minimumPickAlpha = 0, maximumPickScale = 0) {
        this.ray = new Ray(origin, direction);
        this.raycastAgainstTrueSplatEllipsoid = raycastAgainstTrueSplatEllipsoid;
        this.minimumPickAlpha = minimumPickAlpha;
        this.maximumPickScale = maximumPickScale;
        this.hideBlueTintedSplats = false;
        this.areaOfInterestCenter = new THREE.Vector3();
        this.areaOfInterestRadius = 0;
        this.areaOfInterestEnabled = false;
    }

    static isBlueTintedSplat(color) {
        const r = color.x;
        const g = color.y;
        const b = color.z;
        return b > Math.max(r, g) + 0.1 * 255 && b > 0.3 * 255;
    }

    static computeAverageScale(tempScale, splatRenderMode) {
        if (splatRenderMode === SplatRenderMode.ThreeD) {
            return (tempScale.x + tempScale.y + tempScale.z) / 3.0;
        }
        return (tempScale.x + tempScale.y) / 2.0;
    }

    static computePickRadius(tempScale, alpha, splatRenderMode, minimumPickAlpha = 0, pickOptions = null) {
        if (alpha < minimumPickAlpha) return 0;

        const avgScale = Raycaster.computeAverageScale(tempScale, splatRenderMode);
        if (avgScale <= 0) return 0;

        const options = pickOptions || {};
        const pickRadiusMultiplier = options.pickRadiusMultiplier ?? 1.0;

        if (options.useExpandedPickRadius) {
            const opacity = alpha / 255.0;
            const sigmaMultiplier = Math.sqrt(Math.max(0, -2.0 * Math.log(Math.max(PICK_OPACITY_THRESHOLD / opacity, 1e-6))));
            return avgScale * Math.min(sigmaMultiplier, MAX_OPACITY_RADIUS_MULTIPLIER) * pickRadiusMultiplier;
        }

        return avgScale * pickRadiusMultiplier;
    }

    static tryClosestApproachPick(ray, center, radius, outHit, tempVec, tempDir) {

        tempVec.copy(center).sub(ray.origin);
        const t = tempVec.dot(ray.direction);
        if (t < 0) return false;

        tempDir.copy(ray.direction).multiplyScalar(t);
        tempVec.sub(tempDir);
        if (tempVec.lengthSq() > radius * radius) return false;

        outHit.origin.copy(ray.origin).addScaledVector(ray.direction, t);
        outHit.normal.copy(center).sub(outHit.origin);
        if (outHit.normal.lengthSq() > 0) outHit.normal.normalize();
        else outHit.normal.set(0, 1, 0);
        outHit.distance = t;
        return true;
    }

    setFromCameraAndScreenPosition = function() {

        const ndcCoords = new THREE.Vector2();

        return function(camera, screenPosition, screenDimensions) {
            ndcCoords.x = screenPosition.x / screenDimensions.x * 2.0 - 1.0;
            ndcCoords.y = (screenDimensions.y - screenPosition.y) / screenDimensions.y * 2.0 - 1.0;
            if (camera.isPerspectiveCamera) {
                this.ray.origin.setFromMatrixPosition(camera.matrixWorld);
                this.ray.direction.set(ndcCoords.x, ndcCoords.y, 0.5 ).unproject(camera).sub(this.ray.origin).normalize();
                this.camera = camera;
            } else if (camera.isOrthographicCamera) {
                this.ray.origin.set(ndcCoords.x, ndcCoords.y,
                                   (camera.near + camera.far) / (camera.near - camera.far)).unproject(camera);
                this.ray.direction.set(0, 0, -1).transformDirection(camera.matrixWorld);
                this.camera = camera;
            } else {
                throw new Error('Raycaster::setFromCameraAndScreenPosition() -> Unsupported camera type');
            }
        };

    }();

    intersectSplatMesh = function() {

        const toLocal = new THREE.Matrix4();
        const fromLocal = new THREE.Matrix4();
        const sceneTransform = new THREE.Matrix4();
        const localRay = new Ray();
        const tempPoint = new THREE.Vector3();

        return function(splatMesh, outHits = [], pickOptions = Raycaster.MEASUREMENT_PICK_OPTIONS) {
            const splatTree = splatMesh.getSplatTree();

            if (!splatTree) return;

            for (let s = 0; s < splatTree.subTrees.length; s++) {
                const subTree = splatTree.subTrees[s];

                fromLocal.copy(splatMesh.matrixWorld);
                if (splatMesh.dynamicMode) {
                    splatMesh.getSceneTransform(s, sceneTransform);
                    fromLocal.multiply(sceneTransform);
                }
                toLocal.copy(fromLocal).invert();

                localRay.origin.copy(this.ray.origin).applyMatrix4(toLocal);
                localRay.direction.copy(this.ray.origin).add(this.ray.direction);
                localRay.direction.applyMatrix4(toLocal).sub(localRay.origin).normalize();

                const outHitsForSubTree = [];
                if (subTree.rootNode) {
                    this.castRayAtSplatTreeNode(localRay, splatTree, subTree.rootNode, outHitsForSubTree, pickOptions);
                }

                outHitsForSubTree.forEach((hit) => {
                    hit.origin.applyMatrix4(fromLocal);
                    hit.normal.applyMatrix4(fromLocal).normalize();
                    hit.distance = tempPoint.copy(hit.origin).sub(this.ray.origin).length();
                });

                outHits.push(...outHitsForSubTree);
            }

            outHits.sort((a, b) => {
                if (a.distance > b.distance) return 1;
                else return -1;
            });

            return outHits;
        };

    }();

    castRayAtSplatTreeNode = function() {

        const tempColor = new THREE.Vector4();
        const tempCenter = new THREE.Vector3();
        const tempCenterWorld = new THREE.Vector3();
        const tempScale = new THREE.Vector3();
        const tempRotation = new THREE.Quaternion();
        const tempHit = new Hit();
        const scaleEpsilon = 0.0000001;

        const origin = new THREE.Vector3(0, 0, 0);
        const uniformScaleMatrix = new THREE.Matrix4();
        const scaleMatrix = new THREE.Matrix4();
        const rotationMatrix = new THREE.Matrix4();
        const toSphereSpace = new THREE.Matrix4();
        const fromSphereSpace = new THREE.Matrix4();
        const tempRay = new Ray();
        const toCenter = new THREE.Vector3();
        const parallelComponent = new THREE.Vector3();

        return function(ray, splatTree, node, outHits = [], pickOptions = Raycaster.MEASUREMENT_PICK_OPTIONS) {
            if (!ray.intersectBox(node.boundingBox)) {
                return;
            }
            if (node.data && node.data.indexes && node.data.indexes.length > 0) {
                const minimumPickAlpha = Math.max(this.minimumPickAlpha, pickOptions.minimumPickAlphaFloor || 0);

                for (let i = 0; i < node.data.indexes.length; i++) {

                    const splatGlobalIndex = node.data.indexes[i];
                    const splatSceneIndex = splatTree.splatMesh.getSceneIndexForSplat(splatGlobalIndex);
                    const splatScene = splatTree.splatMesh.getScene(splatSceneIndex);
                    if (!splatScene.visible) continue;

                    splatTree.splatMesh.getSplatColor(splatGlobalIndex, tempColor);
                    if (this.areaOfInterestEnabled) {
                        splatTree.splatMesh.getSplatCenter(splatGlobalIndex, tempCenterWorld, true);
                        if (tempCenterWorld.distanceTo(this.areaOfInterestCenter) > this.areaOfInterestRadius) continue;
                    }
                    splatTree.splatMesh.getSplatCenter(splatGlobalIndex, tempCenter);
                    splatTree.splatMesh.getSplatScaleAndRotation(splatGlobalIndex, tempScale, tempRotation);

                    if (tempColor.w < minimumPickAlpha) continue;
                    if (this.hideBlueTintedSplats && Raycaster.isBlueTintedSplat(tempColor)) continue;

                    if (tempScale.x <= scaleEpsilon || tempScale.y <= scaleEpsilon ||
                        splatTree.splatMesh.splatRenderMode === SplatRenderMode.ThreeD && tempScale.z <= scaleEpsilon) {
                        continue;
                    }

                    const avgScale = Raycaster.computeAverageScale(tempScale, splatTree.splatMesh.splatRenderMode);
                    if (this.maximumPickScale > 0 && avgScale > this.maximumPickScale) continue;

                    const pickRadius = Raycaster.computePickRadius(tempScale, tempColor.w,
                                                                     splatTree.splatMesh.splatRenderMode,
                                                                     minimumPickAlpha, pickOptions);
                    if (pickRadius <= 0) continue;

                    let picked = false;

                    if (!this.raycastAgainstTrueSplatEllipsoid) {
                        if (ray.intersectSphere(tempCenter, pickRadius, tempHit)) {
                            picked = true;
                        } else if (pickOptions.allowClosestApproach &&
                                   Raycaster.tryClosestApproachPick(ray, tempCenter, pickRadius, tempHit,
                                                                    toCenter, parallelComponent)) {
                            picked = true;
                        }
                    } else {
                        scaleMatrix.makeScale(tempScale.x, tempScale.y, tempScale.z);
                        rotationMatrix.makeRotationFromQuaternion(tempRotation);
                        const opacity = tempColor.w / 255.0;
                        const opacityScale = Math.sqrt(Math.max(0, -2.0 * Math.log(Math.max(PICK_OPACITY_THRESHOLD / opacity, 1e-6))));
                        const clampedOpacityScale = Math.min(opacityScale, MAX_OPACITY_RADIUS_MULTIPLIER);
                        uniformScaleMatrix.makeScale(clampedOpacityScale, clampedOpacityScale, clampedOpacityScale);
                        fromSphereSpace.copy(uniformScaleMatrix).multiply(rotationMatrix).multiply(scaleMatrix);
                        toSphereSpace.copy(fromSphereSpace).invert();
                        tempRay.origin.copy(ray.origin).sub(tempCenter).applyMatrix4(toSphereSpace);
                        tempRay.direction.copy(ray.origin).add(ray.direction).sub(tempCenter);
                        tempRay.direction.applyMatrix4(toSphereSpace).sub(tempRay.origin).normalize();
                        if (tempRay.intersectSphere(origin, 1.0, tempHit)) {
                            const hitClone = tempHit.clone();
                            hitClone.splatIndex = splatGlobalIndex;
                            hitClone.origin.applyMatrix4(fromSphereSpace).add(tempCenter);
                            outHits.push(hitClone);
                            continue;
                        }
                    }

                    if (picked) {
                        const hitClone = tempHit.clone();
                        hitClone.splatIndex = splatGlobalIndex;
                        outHits.push(hitClone);
                    }
                }
             }
            if (node.children && node.children.length > 0) {
                for (let child of node.children) {
                    this.castRayAtSplatTreeNode(ray, splatTree, child, outHits, pickOptions);
                }
            }
            return outHits;
        };

    }();
}
